import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { Waves } from "lucide-react"
import { authApi } from "../api/authApi"
import { useAuthStore } from "../stores/authStore"
import { ROUTES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"

export function LoginPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      setAuth(data.user)
      navigate(ROUTES.HOME)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    loginMutation.mutate({ email, password })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center justify-center">
            <Waves className="h-20 w-20 text-secondary-500" strokeWidth={1} />
          </div>
          <h1 className="font-roboto text-4xl font-bold text-secondary-500">
            FinHarv
          </h1>
          <p className="font-roboto mt-2 text-base text-gray-600">
            Fish Farming Management
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl bg-white p-8" style={{ border: '1px solid #E0E0E0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)' }}>
          <h2 className="font-roboto mb-6 text-center text-2xl font-semibold text-gray-900">
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={cn(
                  "w-full rounded-lg bg-gray-50 px-4 py-2.5",
                  "text-gray-900 placeholder-gray-400",
                  "focus:outline-none focus:ring-0",
                  "transition-colors duration-200"
                )}
                style={{
                  border: '1px solid #BDBDBD',
                  borderRadius: '8px',
                }}
                onFocus={(e) => e.target.style.borderColor = '#2E7D32'}
                onBlur={(e) => e.target.style.border = '1px solid #BDBDBD'}
                placeholder="you@example.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={cn(
                  "w-full rounded-lg bg-gray-50 px-4 py-2.5",
                  "text-gray-900 placeholder-gray-400",
                  "focus:outline-none focus:ring-0",
                  "transition-colors duration-200"
                )}
                style={{
                  border: '1px solid #BDBDBD',
                  borderRadius: '8px',
                }}
                onFocus={(e) => e.target.style.borderColor = '#2E7D32'}
                onBlur={(e) => e.target.style.border = '1px solid #BDBDBD'}
                placeholder="••••••••"
              />
            </div>

            {/* Error Message */}
            {loginMutation.isError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-800">
                  Invalid email or password. Please try again.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className={cn(
                "font-roboto w-full rounded-lg bg-primary-500 px-4 py-3 font-medium text-base text-white",
                "shadow transition-all duration-200",
                "hover:bg-primary-700 hover:shadow-lg",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2",
                "disabled:cursor-not-allowed disabled:opacity-50",
                "flex items-center justify-center gap-2"
              )}
            >
              {loginMutation.isPending ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Signing In...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

            {/* Divider */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 border-t border-gray-300" />
            <span className="font-roboto text-xs text-gray-600">OR</span>
            <div className="flex-1 border-t border-gray-300" />
          </div>

          {/* Sign Up Button */}
          <button
            type="button"
            onClick={() => navigate(ROUTES.SIGNUP)}
            disabled={loginMutation.isPending}
            className={cn(
              "font-roboto mt-6 w-full rounded-lg bg-white px-4 py-3 font-medium text-gray-900 text-base",
              "shadow-sm transition-all duration-200",
              "hover:bg-gray-50 hover:shadow",
              "focus:outline-none focus:ring-0",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "flex items-center justify-center gap-2"
            )}
            style={{
              border: '1px solid #BDBDBD',
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            <span>Create Account</span>
          </button>
        </div>
      </div>
    </div>
  )
}
